import type { QueryClient } from '@tanstack/react-query';
import type { OrderOption } from '@/features/pages/types/page.types';
import type { CollectionFilter } from '@/features/collections/types/collection.types';

const ROOT_SCOPE = 'root';

export const pageKeys = {
  all: ['pages'] as const,
  lists: () => [...pageKeys.all, 'list'] as const,
  metadata: (orderBy: OrderOption = 'updatedAt') =>
    [...pageKeys.lists(), 'metadata', { orderBy }] as const,
  detail: (pageId: string) => [...pageKeys.all, 'detail', pageId] as const,
  first: (orderBy: OrderOption = 'updatedAt') =>
    [...pageKeys.all, 'detail', 'first', { orderBy }] as const,
  archived: (collectionId?: string) =>
    [...pageKeys.lists(), 'archived', collectionId ?? ROOT_SCOPE] as const,
};

export const collectionKeys = {
  all: ['collections'] as const,
  lists: () => [...collectionKeys.all, 'list'] as const,
  list: (filter: CollectionFilter | 'all' = 'all', tag?: string) =>
    [...collectionKeys.lists(), { filter, tag: tag ?? null }] as const,
  search: (query: string) => [...collectionKeys.all, 'search', query] as const,
  detail: (collectionId: string) => [...collectionKeys.all, 'detail', collectionId] as const,
  structure: (collectionId: string) =>
    [...collectionKeys.detail(collectionId), 'structure'] as const,
  pageMetadata: (collectionId: string, sectionId?: string) =>
    [
      ...collectionKeys.detail(collectionId),
      'pages',
      'metadata',
      { sectionId: sectionId ?? null },
    ] as const,
  publicLists: () => [...collectionKeys.all, 'public'] as const,
};

export const tagKeys = {
  all: ['tags'] as const,
  list: () => [...tagKeys.all, 'list'] as const,
};

export const syncKeys = {
  all: ['sync'] as const,
  metadata: () => [...syncKeys.all, 'metadata'] as const,
  progress: () => [...syncKeys.all, 'progress'] as const,
};

export const publicKeys = {
  all: ['public'] as const,
  subdomainValidation: (subdomain: string | null) =>
    [...publicKeys.all, 'subdomain-validation', subdomain ?? ROOT_SCOPE] as const,
  collections: (subdomain: string | null) =>
    [...publicKeys.all, 'collections', subdomain ?? ROOT_SCOPE] as const,
  collectionFirstPage: (collectionId: string | undefined) =>
    [...publicKeys.all, 'collection-first-page', collectionId ?? ROOT_SCOPE] as const,
  collectionNotesMetadata: (collectionId: string | undefined) =>
    [...publicKeys.all, 'collection-notes', collectionId ?? ROOT_SCOPE, 'metadata'] as const,
  collectionNotesFull: (collectionId: string | undefined) =>
    [...publicKeys.all, 'collection-notes', collectionId ?? ROOT_SCOPE, 'full'] as const,
  note: (pageId: string | undefined, collectionId?: string) =>
    [
      ...publicKeys.all,
      'note',
      pageId ?? ROOT_SCOPE,
      { collectionId: collectionId ?? null },
    ] as const,
};

export const invalidatePageQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: pageKeys.all });

export const invalidateCollectionQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: collectionKeys.all });

export const invalidateTagQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: tagKeys.all });

export const invalidateSyncQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: syncKeys.all });

export const invalidateLocalDataQueries = async (queryClient: QueryClient) => {
  await Promise.all([
    invalidatePageQueries(queryClient),
    invalidateCollectionQueries(queryClient),
    invalidateTagQueries(queryClient),
  ]);
};
